// Copyright 2017 Sorama B.V.

import {
    autoinject,
    bindable,
    BindingEngine,
    bindingMode,
    computedFrom,
    customElement,
    Disposable,
} from "aurelia-framework";
import { TaskQueue } from "aurelia-task-queue";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotParentBase } from "controls/plot/PlotParentBase";
import { Point2D } from "datatypes/SoramaLocalApi";
import { ArrayExtensions } from "extensions/ArrayExtensions";
import { StringExtensions } from "extensions/StringExtensions";
import { IExportable } from "libraries/Export/IExportable";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import { CustomizableAttributeTypes, SVGNode } from "libraries/SVG/SVGNode";
import { Generic } from "units/Generic";
import { Unit } from "units/Unit";
import { Weighting } from "units/Weighting";

export interface Boundary {
    lower: number;
    upper: number;
}

export enum PlotDataTypes {
    LineData,
    BarData,
}

type DisplayValueGetter = (point: Point2D, index: number) => string;
type BarMarginGetter = (barWidth: number) => number;

//#region Exported PlotData Types
export class PlotDataDisplayOptions {
    name: string;
    color: string;
    opacity: number;
    displayValue: DisplayValueGetter;
    displayValueColor: string;
    id: string;
}

export abstract class PlotData {
    abstract readonly type: PlotDataTypes;

    public readonly data: Point2D[];
    public reducedData: Point2D[];

    public readonly xUnit: Unit;
    public readonly yUnit: Unit;
    public readonly weighting: Weighting;

    public readonly options: PlotDataDisplayOptions;
    public readonly boundary: Boundary[];

    constructor(
        data: Point2D[],
        xUnit: Unit,
        yUnit: Unit,
        weighting?: Weighting,
        options?: PlotDataDisplayOptions,
        boundary?: Boundary[]
    ) {
        this.data = data;

        this.xUnit = xUnit;
        this.yUnit = yUnit;
        this.weighting = weighting;

        this.options = options ? options : new PlotDataDisplayOptions();
        this.boundary = boundary;
    }
}

export class LineData extends PlotData {
    static defaultColor = "yellow";
    static defaultOpacity = 1;
    static defaultDisplayValueColor = "white";

    public readonly type = PlotDataTypes.LineData;

    constructor(data: Point2D[], xUnit: Unit, yUnit: Unit, weighting?: Weighting, options?: PlotDataDisplayOptions) {
        super(data, xUnit, yUnit, weighting, options);

        if (!this.options.color) this.options.color = LineData.defaultColor;

        if (this.options.opacity == undefined) this.options.opacity = LineData.defaultOpacity;

        if (!this.options.displayValueColor) this.options.displayValueColor = LineData.defaultDisplayValueColor;
    }
}

export enum BarBottomLocation {
    Origin,
    GraphBottom,
}

export enum BarTextLocation {
    BarBottom,
    BarTop,
    GraphBottom,
    GraphTop,
}

export class BarDataDisplayOptions extends PlotDataDisplayOptions {
    displayValueLocation: BarTextLocation;
    barWidth: number;
    barMargin: number | BarMarginGetter;
    barBottomLocation: BarBottomLocation;
    barLabel: DisplayValueGetter;
    barLabelLocation: BarTextLocation;
    barLabelColor: string;
    barType: string;
}

export class BarData extends PlotData {
    static defaultColor = "#27ae60"; // Flat UI Nephritis
    static defaultOpacity = 0.4;
    static defaultDisplayValueColor = "white";
    static defaultbarBottomLocation = BarBottomLocation.GraphBottom;
    static defaultDisplayValueLocation = BarTextLocation.BarTop;
    static defaultBarLabelLocation = BarTextLocation.BarBottom;
    static defaultBarLabelColor = "white";

    public readonly type = PlotDataTypes.BarData;

    public readonly options: BarDataDisplayOptions;

    constructor(
        data: Point2D[],
        xUnit: Unit,
        yUnit: Unit,
        weighting?: Weighting,
        options?: BarDataDisplayOptions,
        boundary?: Boundary[]
    ) {
        super(data, xUnit, yUnit, weighting, options, boundary);

        if (!this.options.color) this.options.color = BarData.defaultColor;

        if (this.options.opacity == undefined) this.options.opacity = BarData.defaultOpacity;

        if (!this.options.displayValueColor) this.options.displayValueColor = BarData.defaultDisplayValueColor;

        if (this.options.barBottomLocation == undefined)
            this.options.barBottomLocation = BarData.defaultbarBottomLocation;

        if (this.options.displayValueLocation == undefined)
            this.options.displayValueLocation = BarData.defaultDisplayValueLocation;

        if (this.options.barLabelLocation == undefined) this.options.barLabelLocation = BarData.defaultBarLabelLocation;

        if (!this.options.barLabelColor) this.options.barLabelColor = BarData.defaultBarLabelColor;
    }
}
//#endregion

//#region Internal PlotSerie Types
export abstract class PlotSerie {
    public readonly type: PlotDataTypes;
    public readonly name: string;
    public readonly color: string;
    public readonly opacity: number;

    constructor(type: PlotDataTypes, name: string, color: string, opacity: number) {
        this.type = type;
        this.name = name;
        this.color = color;
        this.opacity = opacity;
    }
}

class LineSerie extends PlotSerie {
    private readonly points: string;

    constructor(points: string, name: string, color: string, opacity: number) {
        super(PlotDataTypes.LineData, name, color, opacity);
        this.points = points;
    }
}

class Bar {
    public x: number;
    public y: number;
    public width: number;
    public height: number;
    public displayValue: string;
    public displayValueY: number;
    public displayValueFontSize: number;
    public barLabel: string;
    public barLabelY: number;
    public barLabelFontSize: number;
    public isPrimary: string;

    constructor(
        x: number,
        y: number,
        width: number,
        height: number,
        displayValue: string,
        displayValueY: number,
        displayValueFontSize: number,
        barLabel: string,
        barLabelY: number,
        barLabelFontSize: number,
        barType: string
    ) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.displayValue = displayValue;
        this.displayValueY = displayValueY;
        this.displayValueFontSize = displayValueFontSize;
        this.barLabel = barLabel;
        this.barLabelY = barLabelY;
        this.barLabelFontSize = barLabelFontSize;
        this.isPrimary = barType;
    }
}

class BarSerie extends PlotSerie {
    private readonly bars: Bar[];

    private readonly displayValueColor: string;
    private readonly barLabelColor: string;

    constructor(
        bars: Bar[],
        name: string,
        color: string,
        opacity: number,
        displayValueColor: string,
        barLabelColor: string
    ) {
        super(PlotDataTypes.BarData, name, color, opacity);

        this.bars = bars;
        this.displayValueColor = displayValueColor;
        this.barLabelColor = barLabelColor;
    }
}
//#endregion

@customElement("graph-plot")
@autoinject
export class GraphPlot extends PlotParentBase implements IExportable {
    private static graphPlotIdCounter = 0;
    private readonly controlId: number = 0;

    @computedFrom("controlId")
    private get clipPathId(): string {
        return "graphplot_clippath_" + this.controlId;
    }

    //#region constants
    private lineDataType: PlotDataTypes = PlotDataTypes.LineData;
    private barDataType: PlotDataTypes = PlotDataTypes.BarData;

    private static DefaultBarTextFontSize = 15;
    private static DefaultBarTextYMargin = 3;

    private measureDebounce = 50; // [ms]
    //#endregion

    //#region Injected Services
    private readonly bindingEngine: BindingEngine;
    private readonly taskQueue: TaskQueue;
    private readonly resizeListener: ResizeListener;
    //#endregion

    //#region Bindables
    @bindable public data: PlotData[] = [];

    @bindable({ changeHandler: "axisBoundChanged", defaultBindingMode: bindingMode.oneWay })
    public xAxisBoundStart: number;
    @bindable({ changeHandler: "axisBoundChanged", defaultBindingMode: bindingMode.oneWay })
    public xAxisBoundEnd: number;
    @bindable({ changeHandler: "axisBoundChanged", defaultBindingMode: bindingMode.oneWay })
    public yAxisBoundStart: number;
    @bindable({ changeHandler: "axisBoundChanged", defaultBindingMode: bindingMode.oneWay })
    public yAxisBoundEnd: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay }) public xAxisStart = 0;
    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay }) public xAxisEnd = 1;
    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay }) public yAxisStart = 0;
    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay }) public yAxisEnd = 1;

    @bindable public automaticScaling = false;
    @bindable public legend = false;
    @bindable public stepped = false;

    // Padding of graph in percentage of width/height
    @bindable public paddingLeft = 0;
    @bindable public paddingRight = 0;
    @bindable public paddingTop = 0;
    @bindable public paddingBottom = 0;

    //#endregion

    //#region Fields
    public xUnit = Generic.unitless;
    public yUnit = Generic.unitless;

    private subscriptions: Disposable[];
    private dataSubscription: Disposable;

    private measureViewPortTimer: number;
    private lastMeasureViewPort: Date;
    private isFullScreen: boolean;

    private svgScaleX: number;
    private svgScaleY: number;

    private isAttached: boolean;

    private svgSize: Size = new Size(0, 0);

    private plotSeries: PlotSerie[] = [];

    @computedFrom("plotSeries")
    private get linePlotSeries(): PlotSerie[] {
        return this.plotSeries.filter((x) => x.type === PlotDataTypes.LineData);
    }

    @computedFrom("plotSeries")
    private get barPlotSeries(): PlotSerie[] {
        return this.plotSeries.filter((x) => x.type === PlotDataTypes.BarData);
    }

    @computedFrom("svgSize")
    public get viewBox(): string {
        return this.svgSize ? "0,0," + this.svgSize.width + "," + this.svgSize.height : "0,0,1,1";
    }

    private dataBounds: Rect = new Rect(0, 0, 1, 1);

    private plotContainer: HTMLElement;

    //#endregion

    //#region Constructor and Initialisation

    constructor(bindingEngine: BindingEngine, resizeListener: ResizeListener, taskQueue: TaskQueue) {
        super();

        this.controlId = GraphPlot.graphPlotIdCounter++;
        this.bindingEngine = bindingEngine;
        this.resizeListener = resizeListener;
        this.taskQueue = taskQueue;
        this.subscriptions = [];
    }

    public bind(): void {
        // Subscribe to changes
        this.subscriptions.push(
            this.resizeListener.elementResizeEvent(this.controlContainer, () => this.requestMeasure())
        );

        document.addEventListener("fullscreenchange", this.fullscreenChanged);
    }

    public attached(): void {
        this.isAttached = true;

        // Wait for the layouting to complete before we initialize the rest of the control
        this.taskQueue.queueTask(() => {
            // Initialise control and viewport
            this.measureControl();
            this.calculateDataExtrema();

            this.xAxisBoundStart = this.xAxisBoundStart == undefined ? Number.NEGATIVE_INFINITY : this.xAxisBoundStart;
            this.xAxisBoundEnd = this.xAxisBoundEnd == undefined ? Number.POSITIVE_INFINITY : this.xAxisBoundEnd;
            this.yAxisBoundStart = this.yAxisBoundStart == undefined ? Number.NEGATIVE_INFINITY : this.yAxisBoundStart;
            this.yAxisBoundEnd = this.yAxisBoundEnd == undefined ? Number.POSITIVE_INFINITY : this.yAxisBoundEnd;

            this.viewPortBounds = new Rect(
                this.xAxisBoundStart,
                this.yAxisBoundStart,
                this.xAxisBoundEnd - this.xAxisBoundStart,
                this.yAxisBoundEnd - this.yAxisBoundStart
            );

            this.measureViewPort();
            this.calculatePlotSeries();
        });
    }

    public detached(): void {
        this.isAttached = false;
    }

    public unbind(): void {
        while (this.subscriptions.length > 0) {
            this.subscriptions.pop().dispose();
        }

        if (this.dataSubscription) {
            this.dataSubscription.dispose();
        }

        document.removeEventListener("fullscreenchange", this.fullscreenChanged);
    }

    //#endregion

    //#region IPlotParent
    public requestMeasure(force = false): void {
        if (!this.isAttached) return;

        if (this.measureControl(force)) {
            this.calculatePlotSeries();
        }
    }

    public requestViewPort(): void {
        this.measureViewPort();
    }

    public roundToNearestBin(value: number, axis: string): number {
        if (axis !== "x") {
            return value;
        }

        if (this.data && this.data.length >= 1) {
            // Select the closest bin out of all plotted series. NOTE: using Math.min on the array itself results in a stack size exceeded error
            let closeVal = Number.MAX_VALUE;
            let closeError = Number.MAX_VALUE;

            for (let i = 0; i < this.data.length; i++) {
                const innerData = this.data[i].data;

                for (let j = 0; j < innerData.length; j++) {
                    const error = Math.abs(innerData[j].x - value);
                    if (error < closeError) {
                        closeVal = innerData[j].x;
                        closeError = error;
                    }
                }
            }

            return closeVal;
        }

        return value;
    }

    //#endregion

    //#region IExportable

    public exportToSvg(): Promise<SVGNode> {
        const svg = new SVGNode(this.plotContainer.children[0]);

        // Update viewbox size to include margin
        svg.setAttribute(
            "viewBox",
            StringExtensions.format(
                "{0},{1},{2},{3}",
                (-this.margin.left).toFixed(0),
                (-this.margin.top - 10).toFixed(0),
                (this.svgSize.width + this.margin.left + this.margin.right + 10).toFixed(0),
                (this.svgSize.height + this.margin.top + this.margin.bottom + 10).toFixed(0)
            )
        );

        // Remove width and height properties
        svg.removeAttribute("width");
        svg.removeAttribute("height");
        svg.removeAttribute("preserveAspectRatio");

        // Add a black background
        const backgroundNode = new SVGNode("rect");
        backgroundNode.setAttribute("x", "0");
        backgroundNode.setAttribute("y", "0");
        backgroundNode.setAttribute("width", this.svgSize.width.toFixed(0));
        backgroundNode.setAttribute("height", this.svgSize.height.toFixed(0));
        backgroundNode.setAttribute("fill", "black");
        backgroundNode.addCustomizableAttribute("Background Color", "fill", CustomizableAttributeTypes.color);
        backgroundNode.addCustomizableAttribute("Background Opacity", "opacity", CustomizableAttributeTypes.number);

        svg.addChild(backgroundNode, 0);

        // Update all clip paths
        const newClipPathId = this.clipPathId + "_preview";
        const clipPathNode = svg.find((x) => x.attributes["id"] === this.clipPathId);
        if (clipPathNode) {
            clipPathNode.attributes["id"] = newClipPathId;
        }

        const clippedNodes = svg.findAll((x) => Object.prototype.hasOwnProperty.call(x.attributes, "clip-path"));
        for (const node of clippedNodes) {
            node.attributes["clip-path"] = "url(#" + newClipPathId + ")";
        }

        // For each plot part, check if they implement exportToSvg
        const partPromises = this.plotParts.map((part) => {
            const exportablePart = <IExportable>(<unknown>part);
            if (!exportablePart.exportToSvg) return Promise.resolve(undefined);

            return exportablePart.exportToSvg().then((partSvgNode) => {
                if (partSvgNode) {
                    // Remove SVG parent and replace with g
                    const gNode = new SVGNode("g");
                    for (const child of partSvgNode.children) {
                        gNode.addChild(child);
                    }

                    return gNode;
                }
            });
        });

        return Promise.all(partPromises).then((nodes) => {
            for (const node of nodes.filter((x) => x)) {
                svg.addChild(node);
            }

            return svg;
        });
    }

    //#endregion

    //#region Change Handlers
    private measureControl(force = false): boolean {
        if (!this.controlContainer) return false;

        // Get the size from the containing div element
        const size = new Size(this.controlContainer.clientWidth, this.controlContainer.clientHeight);

        // Measure children
        const margin = new Margin(0, 0, 0, 0);

        this.plotParts.forEach((x) => {
            const partMargin = x.onMeasureMargin();

            margin.left = Math.max(margin.left, partMargin.left);
            margin.top = Math.max(margin.top, partMargin.top);
            margin.right = Math.max(margin.right, partMargin.right);
            margin.bottom = Math.max(margin.bottom, partMargin.bottom);
        });

        // Check if we need a new measure
        if (
            !force &&
            this.size &&
            Math.abs(this.size.width - size.width) < 1e-6 &&
            Math.abs(this.size.height - size.height) < 1e-6 &&
            this.margin &&
            Math.abs(this.margin.left - margin.left) < 1e-6 &&
            Math.abs(this.margin.right - margin.right) < 1e-6 &&
            Math.abs(this.margin.top - margin.top) < 1e-6 &&
            Math.abs(this.margin.bottom - margin.bottom) < 1e-6
        ) {
            return false;
        }

        this.size = size;
        this.margin = margin;

        // Set size of the svg
        this.svgSize = new Size(
            Math.max(this.size.width - margin.left - margin.right, 0),
            Math.max(this.size.height - margin.top - margin.bottom, 0)
        );

        this.svgScaleX = this.svgSize.width / this.viewPort.width;
        this.svgScaleY = this.svgSize.height / this.viewPort.height;

        // Notify children of size and margin change
        this.plotParts.forEach((x) => {
            x.onSize(this.size, this.margin);
        });

        return true;
    }

    private measureViewPort(): boolean {
        const units = this.getCurrentUnits();

        // Determine if the measure has resulted in a new value
        const changes =
            Math.abs(this.viewPort.x - this.xAxisStart) > 1e-6 ||
            Math.abs(this.viewPort.y - this.yAxisStart) > 1e-6 ||
            Math.abs(this.viewPort.width - (this.xAxisEnd - this.xAxisStart)) > 1e-6 ||
            Math.abs(this.viewPort.height - (this.yAxisEnd - this.yAxisStart)) > 1e-6 ||
            units.x != this.xUnit ||
            units.y != this.yUnit;

        if (!changes) {
            return false; // No changes, no need to do an actual update
        }

        // If stepped, make sure the first and last step are shown completely, steps are centered around their x value [-0.5, 0.5]
        const xPadding = this.stepped ? 0.5 : 0;

        this.viewPort = new Rect(
            this.xAxisStart - xPadding,
            this.yAxisStart,
            this.xAxisEnd - this.xAxisStart + xPadding * 2,
            this.yAxisEnd - this.yAxisStart
        );
        this.xUnit = units.x;
        this.yUnit = units.y;

        this.svgScaleX = this.svgSize.width / this.viewPort.width;
        this.svgScaleY = this.svgSize.height / this.viewPort.height;

        this.plotParts.forEach((x) => {
            x.onViewPortComputed(this.viewPort, this.viewPortBounds, new Pair<Unit>(units.x, units.y));
        });

        this.calculatePlotSeries();
        return true;
    }

    public dataChanged(): void {
        // Dispose old subscription and subscribe to new collection changes
        if (this.dataSubscription) {
            this.dataSubscription.dispose();
        }
        this.dataSubscription = this.bindingEngine
            .collectionObserver(this.data)
            .subscribe(() => this.dataCollectionChanged());

        this.dataCollectionChanged();
    }

    public axisBoundChanged(newValue: number, oldValue: number): void {
        if (newValue == oldValue) return;

        this.viewPortBounds = new Rect(
            this.xAxisBoundStart,
            this.yAxisBoundStart,
            this.xAxisBoundEnd - this.xAxisBoundStart,
            this.yAxisBoundEnd - this.yAxisBoundStart
        );

        this.measureViewPort();
    }

    public axisStartOrEndChanged(newValue: unknown, oldValue: unknown): void {
        // Make sure the axis values are valid
        if (
            this.xAxisStart === undefined ||
            this.xAxisStart === null ||
            Number.isNaN(this.xAxisStart) ||
            !Number.isFinite(this.xAxisStart)
        ) {
            this.taskQueue.queueMicroTask(() => {
                this.xAxisStart = 0;
            });
            return;
        }

        if (
            this.yAxisStart === undefined ||
            this.yAxisStart === null ||
            Number.isNaN(this.yAxisStart) ||
            !Number.isFinite(this.yAxisStart)
        ) {
            this.taskQueue.queueMicroTask(() => {
                this.yAxisStart = 0;
            });
            return;
        }

        if (
            this.xAxisEnd === undefined ||
            this.xAxisEnd === null ||
            Number.isNaN(this.xAxisEnd) ||
            !Number.isFinite(this.xAxisEnd)
        ) {
            this.taskQueue.queueMicroTask(() => {
                this.xAxisEnd = 1;
            });
            return;
        }

        if (
            this.yAxisEnd === undefined ||
            this.yAxisEnd === null ||
            Number.isNaN(this.yAxisEnd) ||
            !Number.isFinite(this.yAxisEnd)
        ) {
            this.taskQueue.queueMicroTask(() => {
                this.yAxisEnd = 1;
            });
            return;
        }

        this.measureViewPort();
    }

    private fullscreenChanged = (ev: Event) => {
        this.isFullScreen = !!document.fullscreenElement;
    };
    //#endregion

    //#region User Input Methods
    private onWheel = (event: WheelEvent): void => {};

    private toggleFullScreen(): void {
        if (this.isFullScreen) {
            document.exitFullscreen();
        } else {
            this.controlContainer.requestFullscreen();
        }
    }
    //#endregion

    //#region Helper Methods

    private dataCollectionChanged(): void {
        let viewportUpdated = false;
        this.calculateDataExtrema();

        if (this.automaticScaling) {
            this.viewPortBounds = this.dataBounds.copy();

            this.xAxisStart = this.viewPortBounds.x;
            this.xAxisEnd = this.viewPortBounds.x + this.viewPortBounds.width;
            this.yAxisStart = this.viewPortBounds.y;
            this.yAxisEnd = this.viewPortBounds.y + this.viewPortBounds.height;

            viewportUpdated = this.measureViewPort();
        } else {
            // Check if the units have changed
            const units = this.getCurrentUnits();
            if (units.x !== this.xUnit || units.y !== this.yUnit) {
                viewportUpdated = this.measureViewPort();
            }
        }

        if (!viewportUpdated) {
            // No update from the viewport measurement, update plot series manually
            this.calculatePlotSeries();
        }
    }

    private calculateDataExtrema(): void {
        if (!this.data) {
            return;
        }

        let xMin = Number.POSITIVE_INFINITY;
        let xMax = Number.NEGATIVE_INFINITY;

        let yMin = Number.POSITIVE_INFINITY;
        let yMax = Number.NEGATIVE_INFINITY;

        for (let i = 0; i < this.data.length; i++) {
            const subData = this.data[i];

            for (let j = 0; j < subData.data.length; j++) {
                xMin = Math.min(subData.data[j].x, xMin);
                yMin = Math.min(subData.data[j].y, yMin);

                xMax = Math.max(subData.data[j].x, xMax);
                yMax = Math.max(subData.data[j].y, yMax);
            }
        }

        const width = xMax - xMin;
        const height = yMax - yMin;

        // Take padding into account
        this.dataBounds = new Rect(
            xMin - this.paddingLeft * width,
            yMin - this.paddingBottom * height,
            width + (this.paddingLeft + this.paddingRight) * width,
            height + (this.paddingBottom + this.paddingTop) * height
        );
    }

    private calculatePlotSeries(): void {
        if (!this.data || !this.svgSize || !this.size) return;

        // Add all series to the plot in reversed order
        // so the measurement that is on top of the legend is drawn last (= on-top of the others)
        this.plotSeries = [];
        for (let i = this.data.length - 1; i >= 0; i--) {
            const data = this.data[i];
            switch (data.type) {
                case PlotDataTypes.LineData:
                    data.reducedData = this.reduceData(data.data);
                    this.plotSeries.push(this.calculateLineSerie(<LineData>data));
                    break;

                case PlotDataTypes.BarData:
                    data.reducedData = this.reduceData(data.data, true);
                    this.plotSeries.push(this.calculateBarSerie(<BarData>data));
                    break;
            }
        }
    }

    /*
     * Calculates and returns the points in the data that are actually visible on screen.
     */
    private reduceData(data: Point2D[], maxOnly = false): Point2D[] {
        if (!this.svgSize || !this.size) {
            // Not initialized yet
            return [];
        }

        if (data.length < Math.ceil(this.svgSize.width * 2)) return data.map((x) => this.viewPortToSvgCoordinates(x));

        // Preallocate data for the reduced array based on an estimate of its size
        const reducedData: Point2D[] = new Array(Math.ceil(this.svgSize.width * 2));

        // Find first point
        const firstPoint = ArrayExtensions.fastIndexOf(data, (val: Point2D, index: number, array: Point2D[]) => {
            // Check if we can go right
            if (index === array.length - 1) return 0;

            const v = val.x - this.viewPort.x;
            if (v < 0) return 1;

            // Check if we can go left
            if (index === 0) return 0;

            return array[index - 1].x - this.viewPort.x < 0 && v >= 0 ? 0 : -1;
        });

        // Find last point
        const lastPoint = ArrayExtensions.fastIndexOf(data, (val: Point2D, index: number, array: Point2D[]) => {
            // Check if we can go right
            if (index === array.length - 1) return 0;

            const v = val.x - this.viewPort.x - this.viewPort.width;
            if (v < 0) return 1;

            // Check if we can go left
            if (index === 0) return 0;

            return array[index - 1].x - this.viewPort.x - this.viewPort.width < 0 && v >= 0 ? 0 : -1;
        });

        // Reduce start point with 2 if possible
        let firstVisiblePointIndex = firstPoint <= 0 ? 0 : firstPoint - 1;
        firstVisiblePointIndex = firstVisiblePointIndex === 0 ? 0 : firstVisiblePointIndex - 1;

        // Increment end point with 2 if possible
        let lastVisiblePointIndex = lastPoint === data.length - 1 || lastPoint === -1 ? data.length - 1 : lastPoint + 1;
        lastVisiblePointIndex = lastVisiblePointIndex === data.length - 1 ? data.length - 1 : lastVisiblePointIndex + 1;

        let currentXMin = Number.POSITIVE_INFINITY;
        let currentXMax = Number.NEGATIVE_INFINITY;
        let currentYMin = Number.POSITIVE_INFINITY;
        let currentYMax = Number.NEGATIVE_INFINITY;
        let firstYValue: number = undefined;

        // Start at the point right outside the visibility bounds
        let svgCoord: Point2D = this.viewPortToSvgCoordinates(data[firstVisiblePointIndex]);
        let nextSvgCoord: Point2D = this.viewPortToSvgCoordinates(data[firstVisiblePointIndex + 1]);
        let currentSvgX = nextSvgCoord.x;

        let index = 0;
        for (let i = firstVisiblePointIndex + 1; i <= lastVisiblePointIndex - 1; i++) {
            // Shuffle coordinates
            svgCoord = nextSvgCoord;
            nextSvgCoord = this.viewPortToSvgCoordinates(data[i + 1]);

            if (Math.abs(svgCoord.x - currentSvgX) < 1 && Math.abs(nextSvgCoord.x - currentSvgX) < 1) {
                // This and next svgCoord are located on the same pixel location, determine min and max bounds
                currentXMin = Math.min(svgCoord.x, currentXMin);
                currentXMax = Math.max(svgCoord.x, currentXMax);
                currentYMin = Math.min(svgCoord.y, currentYMin);
                currentYMax = Math.max(svgCoord.y, currentYMax);

                if (firstYValue === undefined) firstYValue = svgCoord.y;
            } else if (currentXMin == Number.POSITIVE_INFINITY) {
                // Only 1 svgCoord in bin, add it
                reducedData[index++] = svgCoord;

                currentSvgX = nextSvgCoord.x;
            } else {
                // Take current svgCoord coordinates into account
                currentXMin = Math.min(svgCoord.x, currentXMin);
                currentXMax = Math.max(svgCoord.x, currentXMax);
                currentYMin = Math.min(svgCoord.y, currentYMin);
                currentYMax = Math.max(svgCoord.y, currentYMax);

                if (firstYValue > svgCoord.y) {
                    const temp = currentYMax;
                    currentYMax = currentYMin;
                    currentYMin = temp;
                }

                // Push current (min and) max
                if (!maxOnly) reducedData[index++] = { x: currentXMin, y: currentYMin };

                reducedData[index++] = { x: currentXMax, y: currentYMax };

                currentSvgX = nextSvgCoord.x;
                currentXMin = Number.POSITIVE_INFINITY;
                currentXMax = Number.NEGATIVE_INFINITY;
                currentYMin = Number.POSITIVE_INFINITY;
                currentYMax = Number.NEGATIVE_INFINITY;

                firstYValue = undefined;
            }
        }

        return reducedData;
    }

    private calculateLineSerie(lineData: LineData): LineSerie {
        const reducedData = lineData.reducedData;
        let points = "";

        if (this.stepped) {
            for (let i = 0; i < reducedData.length; i++) {
                // Given an x[i] and y[i], create a line starting from the halfwaypoint between x[i-1] and x[i] and ending in the halfwaypoint between x[i] and x[i+1]
                // This makes sure our line is always centered its x value on the axis
                // If we don't have a previous value, use the distance to the next value, and vice versa
                const distanceToPrevX = i === 0 ? null : (reducedData[i].x - reducedData[i - 1].x) / 2;
                const distanceToNextX =
                    i === reducedData.length - 1 ? null : (reducedData[i + 1].x - reducedData[i].x) / 2;

                const xStart = reducedData[i].x - (distanceToPrevX ?? distanceToNextX);
                const xEnd = reducedData[i].x + (distanceToNextX ?? distanceToPrevX);

                const y = reducedData[i].y;
                points += xStart.toPrecision(4).toString() + "," + y.toPrecision(4) + " ";
                points += xEnd.toPrecision(4).toString() + "," + y.toPrecision(4) + " ";
            }
        } else {
            // NOTE: Reduced data may contain undefined values because the array was preallocated!
            for (let i = 0; i < reducedData.length; i++) {
                const p = reducedData[i];
                if (!p) break;

                points += p.x.toPrecision(4).toString() + "," + p.y.toPrecision(4) + " ";
            }
        }

        return new LineSerie(points, lineData.options.name, lineData.options.color, lineData.options.opacity);
    }

    private calculateBarSerie(barData: BarData): BarSerie {
        const origin = this.viewPortToSvgCoordinates(<Point2D>{ x: 0, y: 0 });

        const bars = barData.reducedData
            .filter((x) => x !== undefined)
            .map((svgPoint, idx, array) => {
                // Calculate x and y
                const data = this.svgToViewPortCoordinates(svgPoint);
                let x: number;
                const boundary = barData.boundary ? barData.boundary : null;
                const currentBarBoundary = boundary[idx] ? boundary[idx] : null;

                // Calculate wdth and xOffset
                let width = barData.options.barWidth ? barData.options.barWidth * this.svgScaleX : null;

                // For log axis
                if (!barData.xUnit.isLinearUnit) {
                    x =
                        ((Math.log10(currentBarBoundary.lower) - this.viewPort.x) / this.viewPort.width) *
                        this.svgSize.width;
                    const upperValue =
                        ((Math.log10(currentBarBoundary.upper) - this.viewPort.x) / this.viewPort.width) *
                        this.svgSize.width;
                    const lowerValue =
                        ((Math.log10(currentBarBoundary.lower) - this.viewPort.x) / this.viewPort.width) *
                        this.svgSize.width;
                    width = upperValue - lowerValue;
                } else {
                    x = ((currentBarBoundary.lower - this.viewPort.x) / this.viewPort.width) * this.svgSize.width;
                    const upperValue =
                        ((currentBarBoundary.upper - this.viewPort.x) / this.viewPort.width) * this.svgSize.width;
                    const lowerValue =
                        ((currentBarBoundary.lower - this.viewPort.x) / this.viewPort.width) * this.svgSize.width;
                    width = upperValue - lowerValue;
                }

                // Calculate height
                const height =
                    barData.options.barBottomLocation == BarBottomLocation.Origin
                        ? Math.abs(svgPoint.y - origin.y)
                        : Math.max(this.svgSize.height - svgPoint.y, 0);
                const y =
                    (barData.options.barBottomLocation == BarBottomLocation.Origin
                        ? Math.min(svgPoint.y, origin.y)
                        : this.svgSize.height) - height;

                // Calculate margin
                let margin = 0;
                if (typeof barData.options.barMargin === "number") {
                    margin = this.svgSize.width * barData.options.barMargin;
                    margin = Math.min(margin, width / 4);
                } else if (typeof barData.options.barMargin === "function") {
                    margin = barData.options.barMargin(width);
                }

                // Set actual width of bar
                width -= margin * 2;

                // Calculate labels
                const fontSize = GraphPlot.DefaultBarTextFontSize;
                const textMargin = GraphPlot.DefaultBarTextYMargin;

                const displayValueText = barData.options.displayValue ? barData.options.displayValue(data, idx) : "";
                const barLabelText = barData.options.barLabel ? barData.options.barLabel(data, idx) : "";

                // "Guess" the width of the text, if its too large, set font size to small font size, if its still too large, set it to 0 (e.g. don't show the text)
                const displayValueFontSize =
                    (displayValueText.length * GraphPlot.DefaultBarTextFontSize) / 2 < width
                        ? GraphPlot.DefaultBarTextFontSize
                        : (1.5 * width) / displayValueText.length;
                const barLabelFontSize =
                    (barLabelText.length * GraphPlot.DefaultBarTextFontSize) / 2 < width
                        ? GraphPlot.DefaultBarTextFontSize
                        : (1.5 * width) / barLabelText.length;

                let displayValueLocation = this.barTextLocationToCoordinate(
                    barData.options.displayValueLocation,
                    barData.options.barBottomLocation,
                    y,
                    height,
                    origin.y,
                    fontSize,
                    textMargin
                );
                const barLabelLocation = this.barTextLocationToCoordinate(
                    barData.options.barLabelLocation,
                    barData.options.barBottomLocation,
                    y,
                    height,
                    origin.y,
                    fontSize,
                    textMargin
                );

                // Offset displayValueLocation if it overlabs the barLabel
                const textLocationDiff = Math.abs(displayValueLocation - barLabelLocation);
                if (textLocationDiff < barLabelFontSize + textMargin) {
                    displayValueLocation += barLabelFontSize + textMargin - textLocationDiff;
                }

                return new Bar(
                    x + margin,
                    y,
                    width,
                    height,
                    displayValueFontSize > 0 ? displayValueText : "",
                    displayValueLocation,
                    displayValueFontSize,
                    barLabelFontSize > 0 ? barLabelText : "",
                    barLabelLocation,
                    barLabelFontSize,
                    barData.options.barType
                );
            });

        return new BarSerie(
            bars,
            barData.options.name,
            barData.options.color,
            barData.options.opacity,
            barData.options.displayValueColor,
            barData.options.barLabelColor
        );
    }

    private barTextLocationToCoordinate(
        barTextLocation: BarTextLocation,
        barBottomLocation: BarBottomLocation,
        barY: number,
        barHeight: number,
        originY: number,
        fontSize: number,
        margin: number
    ) {
        switch (barTextLocation) {
            case BarTextLocation.BarBottom:
                switch (barBottomLocation) {
                    case BarBottomLocation.Origin:
                        return originY - margin;
                    case BarBottomLocation.GraphBottom:
                        return barY + barHeight - margin;
                    default:
                        break;
                }
                break;
            case BarTextLocation.BarTop:
                return barY - margin;
            case BarTextLocation.GraphBottom:
                return barY + barHeight - margin;
            case BarTextLocation.GraphTop:
                return margin;
        }
    }

    private viewPortToSvgCoordinates(coordinate: Point2D): Point2D {
        return {
            x: (coordinate.x - this.viewPort.x) * this.svgScaleX,
            y: (this.viewPort.height - coordinate.y + this.viewPort.y) * this.svgScaleY,
        };
    }

    private svgToViewPortCoordinates(coordinate: Point2D): Point2D {
        return {
            x: coordinate.x / this.svgScaleX + this.viewPort.x,
            y: -(coordinate.y / this.svgScaleY - this.viewPort.y - this.viewPort.height),
        };
    }

    private getCurrentUnits(): Pair<Unit> {
        let xUnit = this.xUnit;
        let yUnit = this.yUnit;

        if (this.data && this.data.length > 0) {
            xUnit = this.data[0].xUnit;
            yUnit = this.data[0].yUnit;

            for (let i = 1; i < this.data.length; i++) {
                if (this.data[i].xUnit !== xUnit || this.data[i].yUnit !== yUnit) {
                    throw Error("Multiple datas must have the same x- and y- units");
                }
            }
        }

        return new Pair<Unit>(xUnit, yUnit);
    }
    //#endregion
}
